import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import type { OnChanges, SimpleChanges } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WebComponentsLoaderService } from '@shared/webcomponents/webcomponents-loader/webcomponents-loader.service';
import type { IconType } from '../types/IconType';
import { DevelopmentService, UserService } from '@shared/services';
import { combineLatest, filter, startWith } from 'rxjs';
import { Location } from '@angular/common';
import { defaultFeatureFlags, type FeatureFlags } from '@shared/services/feature-flag/feature-flags';
import { FeatureFlagService } from '@shared/services/feature-flag/feature-flag.service';

export interface TabItem {
  label: string;
  url: string;
  count?: number;
  icon?: IconType;
  flag?: 'admin' | FeatureFlags;
}

export interface TabsConfig {
  tabs: TabItem[];
  active?: number;
  theme?: 'light' | 'dark';
}

export interface TabsSelectionResult {
  index: number;
}

@UntilDestroy()
@Component({
  selector: 'app-cw-tabs',
  template: '<rc-cw-tabs [attr.config]="config | json" (select)="onSelect($event)"></rc-cw-tabs>',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CWTabsComponent implements OnChanges {
  @Input() tabs: TabItem[];
  @Input() theme: 'light' | 'dark' = 'light';

  public config: TabsConfig;

  private hideAdmin: boolean;
  private highlight: boolean;
  private flags: Record<FeatureFlags, boolean>;

  constructor(
    private webComponentLoader: WebComponentsLoaderService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private developmentService: DevelopmentService,
    private userService: UserService,
    private location: Location,
    private featureFlagService: FeatureFlagService,
  ) {
    this.webComponentLoader.loadDefaultComponents();

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        this.updateTabs();
      });

    combineLatest([
      this.developmentService.hideAdminContent.pipe(startWith(false)),
      this.developmentService.highlightMode.pipe(startWith(false)),
      this.featureFlagService.featureFlags$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([hideAdmin, highlight, flags]) => {
        this.hideAdmin = hideAdmin;
        this.highlight = highlight;
        this.flags = flags;
        this.updateTabs();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes) return;
    this.updateTabs();
  }

  public onSelect(event: any) {
    event.stopPropagation();
    this.router.navigate([this.config.tabs[event.detail.index].url], { relativeTo: this.route });
  }

  private updateTabs = () => {
    if (!this.tabs) return;

    const tabs: TabItem[] = this.tabs
      .filter((tab) => {
        switch (tab.flag) {
          case 'admin':
            return this.userService.isAdmin() && !this.hideAdmin;
          default:
            if (Object.keys(defaultFeatureFlags).includes(tab.flag)) {
              return this.flags ? this.flags[tab.flag] : defaultFeatureFlags[tab.flag];
            }
            return true;
        }
      })
      .map((tab) => {
        if (tab.flag && !this.highlight) {
          return {
            ...tab,
            flag: undefined,
          };
        }
        return tab;
      });

    const active = tabs.findIndex((item) => item.url === this.location.path());

    this.config = {
      tabs,
      active,
      theme: this.theme,
    };
    this.cdr.markForCheck();
  };
}
